"use client";

import { Button } from "@/components/ui/button";
import {
  CheckCircle2,
  Globe2,
  ShieldCheck,
  Sparkles,
  Video,
} from "lucide-react";
import { useJsonTranslation } from "@/hooks/useJsonTranslation";
import { PublicLink as Link } from "@/components/public/PublicLink";
import { rewriteMarketingHref } from "@/lib/seo/public-seo";

const productPages = [
  {
    key: "dashboard",
    titleKey: "pages.dashboard.title",
    descriptionKey: "pages.dashboard.description",
    href: "/dashboard",
    icon: Sparkles,
    highlightKeys: [
      "pages.dashboard.highlights.0",
      "pages.dashboard.highlights.1",
      "pages.dashboard.highlights.2",
      "pages.dashboard.highlights.3",
    ],
  },
  {
    key: "videoUpload",
    titleKey: "pages.videoUpload.title",
    descriptionKey: "pages.videoUpload.description",
    href: "/projects/new",
    icon: Video,
    highlightKeys: [
      "pages.videoUpload.highlights.0",
      "pages.videoUpload.highlights.1",
      "pages.videoUpload.highlights.2",
      "pages.videoUpload.highlights.3",
    ],
  },
  {
    key: "videoOptimization",
    titleKey: "pages.videoOptimization.title",
    descriptionKey: "pages.videoOptimization.description",
    href: "/projects/new",
    icon: CheckCircle2,
    highlightKeys: [
      "pages.videoOptimization.highlights.0",
      "pages.videoOptimization.highlights.1",
      "pages.videoOptimization.highlights.2",
      "pages.videoOptimization.highlights.3",
    ],
  },
  {
    key: "autoTranslation",
    titleKey: "pages.autoTranslation.title",
    descriptionKey: "pages.autoTranslation.description",
    href: "/projects/new",
    icon: Globe2,
    highlightKeys: [
      "pages.autoTranslation.highlights.0",
      "pages.autoTranslation.highlights.1",
      "pages.autoTranslation.highlights.2",
      "pages.autoTranslation.highlights.3",
    ],
  },
  {
    key: "thumbnailGenerator",
    titleKey: "pages.thumbnailGenerator.title",
    descriptionKey: "pages.thumbnailGenerator.description",
    href: "/thumbnails",
    icon: Sparkles,
    highlightKeys: [
      "pages.thumbnailGenerator.highlights.0",
      "pages.thumbnailGenerator.highlights.1",
      "pages.thumbnailGenerator.highlights.2",
      "pages.thumbnailGenerator.highlights.3",
    ],
  },
  {
    key: "dubbing",
    titleKey: "pages.dubbing.title",
    descriptionKey: "pages.dubbing.description",
    href: "/projects/new",
    icon: ShieldCheck,
    highlightKeys: [
      "pages.dubbing.highlights.0",
      "pages.dubbing.highlights.1",
      "pages.dubbing.highlights.2",
      "pages.dubbing.highlights.3",
    ],
  },
  {
    key: "midrollAds",
    titleKey: "pages.midrollAds.title",
    descriptionKey: "pages.midrollAds.description",
    href: "/projects/new",
    icon: ShieldCheck,
    highlightKeys: [
      "pages.midrollAds.highlights.0",
      "pages.midrollAds.highlights.1",
      "pages.midrollAds.highlights.2",
      "pages.midrollAds.highlights.3",
    ],
  },
];

export default function ProductPage() {
  const { t } = useJsonTranslation("ProductPage");

  return (
    <div className="flex-1 py-12 pt-24 sm:py-16 lg:py-20">
      <div className="container mx-auto px-4">
        {/* Hero Section */}
        <div className="mx-auto max-w-4xl space-y-4 text-center">
          <p className="text-leaf-500 text-sm font-semibold tracking-wide uppercase">
            {t("hero.eyebrow")}
          </p>
          <h1 className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
            {t("hero.title")}
          </h1>
          <p className="text-muted-foreground text-lg leading-relaxed">
            {t("hero.subtitle")}
          </p>
          <div className="flex flex-col items-center justify-center gap-3 pt-2 sm:flex-row">
            <Link href="/signup">
              <Button className="bg-leaf-500 hover:bg-leaf-600 h-12 px-7">
                {t("hero.ctaStartFree")}
              </Button>
            </Link>
            <Link href="/seeds">
              <Button
                variant="outline"
                className="border-border hover:bg-foreground/10 h-12 px-7"
              >
                {t("hero.ctaViewPricing")}
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Product Pages Grid */}
      <section className="mt-12 pb-12 sm:pb-16 lg:pb-20">
        <div className="container mx-auto px-4">
          <div className="mx-auto max-w-5xl">
            <div className="grid gap-6 sm:gap-8">
              {productPages.map((page) => {
                const Icon = page.icon;
                return (
                  <div
                    key={page.key}
                    className="glass border-border rounded-2xl border p-6 sm:p-8"
                  >
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="flex gap-4">
                        <div className="bg-leaf-500/10 text-leaf-600 dark:text-leaf-400 grid h-12 w-12 flex-shrink-0 place-items-center rounded-xl">
                          <Icon className="h-6 w-6" />
                        </div>
                        <div>
                          <h2 className="text-foreground text-xl font-semibold sm:text-2xl">
                            {t(page.titleKey)}
                          </h2>
                          <p className="text-muted-foreground mt-2">
                            {t(page.descriptionKey)}
                          </p>
                          <ul className="text-foreground/80 mt-3 list-inside list-disc space-y-1 text-sm">
                            {page.highlightKeys.map((itemKey, idx) => (
                              <li key={idx}>{t(itemKey)}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                      <div className="flex flex-col gap-3 sm:items-end">
                        <Link href={rewriteMarketingHref(page.href)}>
                          <Button
                            variant="secondary"
                            className="bg-leaf-500 hover:bg-leaf-600 w-full sm:w-auto"
                          >
                            {t("cards.openPage")}
                          </Button>
                        </Link>
                        <Link href="/signup">
                          <Button
                            variant="ghost"
                            className="text-leaf-600 dark:text-leaf-400 hover:text-leaf-600 dark:hover:text-leaf-300 hover:bg-leaf-500/10"
                          >
                            {t("cards.startFree")}
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Closing CTA */}
      <section className="pb-16">
        <div className="container mx-auto px-4">
          <div className="border-border mx-auto max-w-4xl rounded-2xl border p-8 text-center sm:p-10">
            <p className="text-muted-foreground text-sm font-semibold tracking-widest uppercase">
              {t("closing.eyebrow")}
            </p>
            <h3 className="text-foreground text-wrap-balance mt-2 text-2xl font-bold sm:text-3xl">
              {t("closing.title")}
            </h3>
            <p className="text-muted-foreground mt-3 leading-relaxed">
              {t("closing.subtitle")}
            </p>
            <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
              <Link href="/signup">
                <Button className="bg-leaf-500 hover:bg-leaf-600 h-12 px-8">
                  {t("closing.ctaStartFree")}
                </Button>
              </Link>
              <Link href="/contact">
                <Button variant="outline" className="h-12 px-8">
                  {t("closing.ctaContactSales")}
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
